import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getStoredToken, getStoredRefreshToken, clearStoredToken, updateStoredToken, updateStoredRefreshToken } from '../utils/auth';

interface ApiError extends Error {
  status?: number;
  code?: string;
  details?: Record<string, unknown>;
  validationErrors?: Record<string, string>;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface RefreshResult {
  token: string;
  refreshToken: string;
  expiresIn: number;
}

class Api {
  private instance: AxiosInstance;
  private retryCount: number = 0;
  private readonly maxRetries: number = 3;
  private refreshPromise: Promise<RefreshResult> | null = null;

  constructor() {
    // Normalize base URL and ensure it includes /api suffix
    const rawBase = process.env.NEXT_PUBLIC_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const trimmed = rawBase.replace(/\/+$/, '');
    const baseWithApi = trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;

    this.instance = axios.create({
      baseURL: baseWithApi,
      timeout: 30000, // Increased from 10000 to 30000 (30 seconds)
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config): InternalAxiosRequestConfig => {
        const token = getStoredToken();
        if (token && config.headers) {
          // Make sure the token is valid before setting it
          if (token.trim() && /^[A-Za-z0-9-_=]+\.[A-Za-z0-9-_=]+\.[A-Za-z0-9-_.+/=]*$/.test(token)) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            console.warn('🔑 API request interceptor - Invalid token format, not setting Authorization header');
          }
        } else {
        }
        return config;
      },
      (error: AxiosError): Promise<AxiosError> => {
        return Promise.reject(this.handleError(error));
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse): AxiosResponse => {
        this.retryCount = 0; // Reset retry count on successful response
        return response;
      },
      async (error: AxiosError): Promise<AxiosError> => {
        if (this.shouldRetry(error) && error.config) {
          this.retryCount++;
          return this.instance.request(error.config);
        }

        // Only handle 401 for non-auth endpoints
        const requestUrl = error.config?.url || '';
        const isAuthEndpoint = requestUrl.includes('/auth/login') || 
                               requestUrl.includes('/auth/register') ||
                               requestUrl === '/auth/email/verify' ||
                               requestUrl.includes('/auth/password') ||
                               requestUrl.includes('/auth/apple') ||
                               requestUrl.includes('/auth/google') ||
                               requestUrl.includes('/auth/social/complete') ||
                               requestUrl.includes('/auth/refresh-token');

        if (error.response?.status === 401 && !isAuthEndpoint && error.config) {
          // Try refreshing the token before logging out
          if (!error.config.headers?.['X-Retry']) {
            try {
              const { token: newToken } = await this.refreshAccessToken();
              // Retry the original request with the new token
              error.config.headers = error.config.headers || {};
              error.config.headers['Authorization'] = `Bearer ${newToken}`;
              error.config.headers['X-Retry'] = 'true';
              return this.instance.request(error.config);
            } catch {
              // Refresh failed — now actually logout
              clearStoredToken();
              window.location.href = '/';
            }
          } else {
            // Already retried once with a fresh token and still 401 — logout
            clearStoredToken();
            window.location.href = '/';
          }
        }

        return Promise.reject(this.handleError(error));
      }
    );
  }

  /**
   * Refresh the access token using the stored refresh token.
   *
   * This is the ONLY place that calls POST /auth/refresh-token, and it's
   * public specifically so AuthContext's proactive refresh timer and this
   * class's own reactive 401-retry share the exact same in-flight promise.
   * Refresh tokens are single-use and rotated server-side, so two
   * independent, uncoordinated refresh calls racing to use the same token
   * would cause whichever one loses the race to be rejected as "already
   * used" and force a spurious logout. That race is exactly what tended to
   * happen when a backgrounded iOS Safari tab resumes: the proactive timer
   * (paused while backgrounded) fires its overdue refresh at the same
   * moment the first request-after-resume gets a 401 and triggers a
   * reactive refresh. Deduplicating here means both callers just await the
   * same request and get the same new tokens.
   */
  public async refreshAccessToken(): Promise<RefreshResult> {
    if (this.refreshPromise) return this.refreshPromise;

    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) {
      return Promise.reject(new Error('No refresh token available'));
    }

    const rawBase = process.env.NEXT_PUBLIC_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const base = rawBase.replace(/\/+$/, '');
    const refreshUrl = base.endsWith('/api') ? `${base}/auth/refresh-token` : `${base}/api/auth/refresh-token`;

    this.refreshPromise = axios
      .post(refreshUrl, { refreshToken }, { withCredentials: true })
      .then((res) => {
        const body = res.data?.data || res.data;
        // Server may return 'token' or 'accessToken' depending on the endpoint
        const newToken = body.token || body.accessToken;
        const newRefresh = body.refreshToken;
        const expiresIn = body.expiresIn || 3600;
        if (!newToken) {
          throw new Error('No token in refresh response');
        }
        updateStoredToken(newToken, expiresIn);
        if (newRefresh) updateStoredRefreshToken(newRefresh);
        return { token: newToken as string, refreshToken: newRefresh as string, expiresIn };
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  private shouldRetry(error: AxiosError): boolean {
    // Completion consumes a single-use handle. A lost response must not
    // silently replay account creation or credential attachment.
    if (error.config?.url?.includes('/auth/social/complete') ||
        error.config?.url?.includes('/auth/email/verify')) return false;
    const shouldRetry = 
      this.retryCount < this.maxRetries &&
      error.response?.status !== 401 &&
      error.response?.status !== 403 &&
      error.response?.status !== 404 &&
      (error.code === 'ECONNABORTED' || 
       error.response?.status === 408 || 
       error.response?.status === 500 ||
       error.response?.status === 502 ||
       error.response?.status === 503 ||
       error.response?.status === 504);

    return shouldRetry;
  }

  private handleError(error: AxiosError): ApiError {
    // Public methods also catch already-normalized interceptor failures.
    // Preserve their status/code so expired pending identities can restart.
    if (!error.response && !axios.isAxiosError(error)) return error as ApiError;
    const body = error.response?.data as {
      message?: string;
      errors?: { field: string; message: string }[];
    } | undefined;
    const errors = body?.errors;
    const fieldErrors = Array.isArray(errors)
      ? errors.filter(item => item && typeof item.field === 'string' && typeof item.message === 'string')
      : [];
    const apiError: ApiError = new Error(
      body?.message || fieldErrors.map(item => item.message).join(' ') ||
      error.message || 'An unexpected error occurred'
    );

    if (error.response) {
      apiError.status = error.response.status;
      apiError.code = (error.response.data as any)?.code;
      apiError.details = (error.response.data as any)?.details;
      if (fieldErrors.length) {
        apiError.validationErrors = Object.fromEntries(fieldErrors.map(item => [item.field, item.message]));
      }
    }

    return apiError;
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.instance.get<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  public async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.instance.post<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  public async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.instance.put<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  public async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.instance.patch<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.instance.delete<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error as AxiosError);
    }
  }

  public setHeader(name: string, value: string): void {
    this.instance.defaults.headers.common[name] = value;
  }

  public removeHeader(name: string): void {
    delete this.instance.defaults.headers.common[name];
  }
}

export const api = new Api();
